<script setup lang="ts" generic="T extends AcceptableValue">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { AcceptableValue } from 'reka-ui'
import { faCheck, faChevronDown, faTimes } from '@fortawesome/pro-light-svg-icons'
import { useElementSize } from '@vueuse/core'
import {

  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  Label,
} from 'reka-ui'

const props = withDefaults(
  defineProps<{
    options: T[] | ReadonlyArray<T>
    placeholder?: string
    iconLeft?: IconDefinition
    label?: string
    id?: string
    badge?: boolean
    clearable?: boolean
    searchable?: boolean
    context?: 'download' | 'workspace' | 'default' | 'alphabet' | 'legal'
    multiple?: boolean
    intent?: 'filled' | 'outlined'
    contentAlign?: 'start' | 'end'
    optionLabel?: (option: T) => string
    optionIcon?: (option: T) => IconDefinition
  }>(),
  {
    placeholder: 'Søk...',
    multiple: true,
    badge: true,
    clearable: false,
    intent: 'outlined',
    contentAlign: 'start',
    searchable: false,
    optionIcon: undefined,
    context: 'default',
    optionLabel: (option: T) => JSON.stringify(option),
  },
)

const model = defineModel<T[] | T | undefined>()

const searchTerm = ref<string>('')

const filtered = computed(() => {
  if (!searchTerm.value.trim()) {
    return props.options
  }

  const search = searchTerm.value.trim().toLowerCase()
  return props.options.filter((option) => {
    const label = props.optionLabel(option).toLowerCase()
    return label.startsWith(search)
  })
})

const anchorElement = ref<HTMLElement | null>(null)
const { width: anchorWidth } = useElementSize(anchorElement, undefined, {
  box: 'border-box',
})

const widthStyles = computed(() => {
  return {
    minWidth: `${anchorWidth.value}px`,
  }
})

const selectedCount = computed(() => {
  if (Array.isArray(model.value)) {
    return model.value.length
  }
  else {
    return model.value ? 1 : 0
  }
})

const hasSelection = computed(() => selectedCount.value > 0)

const leftIcon = computed(() => hasSelection.value ? faTimes : props.iconLeft)

function onReset() {
  model.value = props.multiple ? [] : undefined
}

function handleDisplayValue() {
  if (props.badge) {
    return props.placeholder
  }

  if (Array.isArray(model.value)) {
    if (hasSelection.value) {
      return `${model.value.length} valgte`
    }
  }
  else if (model.value) {
    return props.optionLabel(model.value)
  }

  return props.placeholder
}
</script>

<template>
  <ComboboxRoot
    v-slot="{ open }"
    v-model="model"
    :multiple="multiple"
    ignore-filter
    class="relative"
  >
    <Label :for="id" class="sr-only">{{ label }}</Label>
    <ComboboxAnchor
      ref="anchorElement"
      :class="comboboxVariants.anchor.base({ open, selected: hasSelection, intent })"
    >
      <component
        :is="hasSelection ? 'button' : 'div'"
        v-if="iconLeft || (clearable && hasSelection)"
        :aria-label="hasSelection ? 'Nullstill' : undefined"
        class="flex items-center justify-center"
        :class="comboboxVariants.anchor.trigger({ selected: hasSelection, intent })"
        @click.stop="onReset"
      >
        <Icon v-if="leftIcon" :icon="leftIcon" />
      </component>
      <!-- TODO: We should try avoid wrapping the input element inside ComboboxTrigger, not UU friendly -->
      <ComboboxTrigger :class="comboboxVariants.anchor.trigger({ selected: hasSelection, intent })" class="flex items-center w-full gap-xs">
        <div class="flex items-center gap-2xs flex-1">
          <!-- Invisible span mirrors the display value so the input (which cannot shrink-to-fit) sizes to its text -->
          <div v-if="!open || !searchable" class="relative flex-1">
            <span aria-hidden="true" class="invisible block whitespace-pre text-small">{{ handleDisplayValue() }}</span>
            <ComboboxInput
              :id="id"
              readonly
              :placeholder="placeholder"
              :display-value="handleDisplayValue"
              class="absolute inset-0 cursor-pointer caret-transparent"
              :class="comboboxVariants.anchor.input({ selected: hasSelection, intent })"
            />
          </div>
          <ComboboxInput
            v-if="open && searchable"
            :id="id"
            v-model="searchTerm"
            :placeholder="hasSelection ? placeholder : 'Søk...'"
            :auto-focus="open"
            :class="comboboxVariants.anchor.input({ selected: hasSelection, intent, searchable })"
          />
          <Badge
            v-if="badge && hasSelection"
            variant="neutral"
            size="small"
            :label="String(selectedCount)"
            class="bg-blurple-300 text-inverse rounded-md shrink-0"
          />
        </div>
        <Icon
          :icon="faChevronDown"
          :class="{ 'rotate-180': open }"
          class="transition-all duration-500"
        />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <Transition name="slide">
      <ComboboxContent
        data-content
        :class="comboboxVariants.content.base({ context, align: contentAlign })"
        :style="widthStyles"
      >
        <ComboboxViewport>
          <ComboboxEmpty
            v-if="searchable && searchTerm.trim().length > 0 && !filtered.length"
            :class="comboboxVariants.content.empty()"
          >
            Ingen resultater...
          </ComboboxEmpty>

          <div v-else-if="!filtered.length" :class="comboboxVariants.content.empty()">
            Ingen alternativer
          </div>

          <ComboboxItem
            v-for="(option, index) in filtered"
            :key="index"
            :value="option"
            :class="comboboxVariants.content.item({ intent, context })"
          >
            <div class="flex items-center gap-s" v-text="props.optionLabel(option)" />
            <div class="size-5 shrink-0" data-placeholder :class="{ hidden: intent === 'filled' || !multiple }">
              <ComboboxItemIndicator :class="comboboxVariants.content.indicator({ context })">
                <Icon :icon="faCheck" class="text-white" size="sm" />
              </ComboboxItemIndicator>
            </div>
          </ComboboxItem>
        </ComboboxViewport>
      </ComboboxContent>
    </Transition>
  </ComboboxRoot>
</template>

<style scoped lang="postcss">
.slide-enter-active {
  @apply transition-all duration-500 ease-out;
}

.slide-leave-active {
  @apply transition-all duration-300 ease-in;
}

.slide-enter-from {
  @apply opacity-0 translate-y-[-2.5xs];
}

.slide-leave-to {
  @apply opacity-0 translate-y-[-2.5xs];
}

[data-state='unchecked'] [data-placeholder] {
  @apply border border-carbon-300 rounded;
}
</style>
